import storage from '../storage.js'
import { Auth } from 'aws-amplify'
import API from '@/lib/api.js'
import file from '@/lib/file.js'
import config from '../../config.js'
import i18n from '@/lib/i18n.js'

const auth = {
  namespaced: true,
  state: {
    authenticated: false,
    definitions: [],
    cognitoUser: null,
    username: null,
    application: null,
    role: null,
    ai_auth: null,
    email: null,
    nickname: null,
    sub: null,
    selectedApplication: null,
    isSuperUser: false,
    isManager: false,
    isResearcher: false,
    isEditor: false,
    isViewer: false,
    isMobile: false,
    isSpecialist: false,
    session: null
  },

  // getters
  getters: {
    definition: (state) => (key) => {
      let d = state.definitions[key]
      if (!d) {
        storage.removeItem(storage.LocalStorageKeys.definitions)
        return undefined
      }
      if (!d.meaning) return {}
      return d.meaning
    },

    currentPageApplication: (state) => () => {
      if (state.isSuperUser) {
        return state.selectedApplication
      } else {
        return state.application
      }
    }
  },

  // actions
  actions: {
    async signIn({ commit, dispatch }, payload) {
      commit('authenticated', false)
      storage.removeAllItems()

      if (!payload) payload = {}

      let bytes = Number(JSON.stringify(payload).length)
      let mega = 1048576
      if ((bytes / mega) > 2) throw new Error('size over')

      if (!payload.user || !payload.pass) {
        dispatch('error', { message: 'Need to Input ID & Password' })
        return
      }
      return await Auth.signIn(payload.user, payload.pass)
    },

    async authenticate({ commit, state, dispatch }) {
      if (state.authenticated && storage.isDefinitionReady) {
        return
      }
      let user = state.cognitoUser
      let session, attribute

      if (config.localMode()) {
        await config.callLocal('user').then((re) => {
          user = re.user
          session = re.session
          attribute = re.attribute
        }).catch((e) => {
          console.log(e)
        })

      } else {
        if (!user) {
          user = await Auth.currentAuthenticatedUser().catch(() => undefined)
          if (!user) {
            dispatch('error', { message: 'No current user' })
            return
          }
        }
        attribute = await Auth.userAttributes(user).catch(
          (err) => {
            dispatch('error', { message: 'No user attribute', err: err })
            return
          }
        )
        for (let i = 0; i < attribute.length; i++) {
          if(attribute[i].getName() == 'custom:status') {
            if(attribute[i].getValue() != 'valid'){
              dispatch('error', { message: 'User is no valid' })
              return
            }
          }
        }

        session = await Auth.userSession(user).catch(
          (err) => {
            dispatch('error', { message: 'No session available', err: err })
            return
          }
        )
      }
      commit('setCognitoUser', user)
      commit('setAttribute', attribute)
      commit('setUserSession', session)

      let localDef = storage.getItem(storage.LocalStorageKeys.definitions)
      if (localDef) {
        let definitions = localDef
        Object.keys(localDef).forEach(d => {
          if (d.indexOf('language') == -1) return
          const def = localDef[d]
          const locale = d.substr(d.indexOf(':') + 1)
          i18n.setMessage(locale, def.meaning)
        })
        commit('setDefinitions', definitions)
      } else {
        // dispatch('loadDefinition')
        let data = await dispatch('call', { api: 'getDefinitions', body: {} }).catch(
          (err) => {
            dispatch('error', { message: 'No definiton available', err: err })
            return
          }
        )
        // 本番／検証環境の場合、一般ユーザーでアクセスするにはprodで指定したアプリケーションのみ有効
        if (['prod', 'staging'].includes(config.getStage())) {
          const applications = JSON.parse(data).find(item => item.application == 'common' && item.label == 'application')
          const prodList = applications.meaning.prod.map(val => val.code)
          if (!state.isSuperUser && !prodList.includes(state.application)) {
            dispatch('error', { message: 'User is not allowed' })
            dispatch('signOut', null)
            return
          }
        }

        JSON.parse(data).forEach(d => {
          if (d.application !== 'language') return
          i18n.setMessage(d.label, d.meaning)
        })

        const definitions = await dispatch('fixRawData', data)
        commit('setDefinitions', definitions)
      }
      commit('authenticated', true)
      commit('setUserAgent')
    },

    async signOut({ commit }, router) {
      await Auth.signOut()
        .then(data => console.log('signOut'))
        .catch(err => console.log(err))
      commit('clearAllState')
      storage.removeAllItems()
      if (router) {
        router.push('/login').catch(() => {})
        i18n.setDefaultLocale()
      }
    },

    async forgotPassword({ dispatch }, payload) {
      storage.removeAllItems()

      if (!payload.user) {
        dispatch('error', { message: 'Need to Input ID' })
        return
      }
      return await Auth.forgotPassword(payload.user)
    },

    async forgotPasswordSubmit({ dispatch }, payload) {
      if (!payload.user || !payload.code || !payload.new_password || !payload.confirm_new_password) {
        dispatch('error', { message: 'Need to Input Verification code & New password' })
        return
      } else if (payload.new_password !== payload.confirm_new_password) {
        dispatch('error', { message: 'The password you entered does not match' })
        return
      }
      return await Auth.forgotPasswordSubmit(payload.user, payload.code, payload.new_password)
    },

    async changePassword({ dispatch }, payload) {
      let user

      if (!payload.old_password || !payload.new_password || !payload.confirm_new_password) {
        dispatch('error', { message: 'Need to Input Verification & Old password & New password' })
        return
      } else if (payload.new_password !== payload.confirm_new_password) {
        dispatch('error', { message: 'The password you entered does not match' })
        return
      }

      user = await Auth.currentAuthenticatedUser().catch(() => undefined)
      if (!user) {
        dispatch('error', { message: 'No current user' })
        return
      }
      return await Auth.changePassword(user, payload.old_password, payload.new_password)
    },

    error({ commit }, payload) {
      commit('clearAllState')
      console.log(payload.message)
      if (payload.err) console.log('err :' + payload.err)
      throw Error(payload.message)
    },

    async loadDefinition({ commit, dispatch }) {
      let data = await dispatch('call', { api: 'getDefinitions', body: {} }).catch(
        (err) => {
          dispatch('error', { message: 'No definiton available', err: err })
          return
        }
      )
      const definitions = await dispatch('fixRawData', data)
      commit('setDefinitions', definitions)
    },

    /** APIから取得したDefinitionsデータを保存用形式に変換 */
    fixRawData({ state }, raw) {
      const definitions = {}
      for (let v of JSON.parse(raw)) {
        const { application, label, ...contents } = v
        // 一般ユーザーの場合は最低限の情報のみ保持する
        definitions[`${application}:${label}`] = state.isSuperUser ? contents : { 'meaning':  v.meaning, 'type': v.type }
      }
      return definitions
    },

    async call({ commit, state, dispatch }, payload) {
      if (!payload) payload = {}
      var session = await Auth.currentSession()
        .catch(err => {
          dispatch('error', { message: 'No session available', err: err })
          return
        })
      commit('setUserSession', session)
      let body = {}
      if (payload.body) body = payload.body
      let header = {
        'Content-Type': 'application/json'
      }
      let bytes = Number(JSON.stringify(body).length)
      let mega = 1048576
      if ((bytes / mega) > 2) throw new Error('size over')
      if (!payload.public && !config.localMode()) {
        header.Authorization = state.session.getIdToken().getJwtToken()
      }
      if (state.isSuperUser) {
        if (!body) body = {}
        body.selectedApplication = state.selectedApplication
      }

      if (config.localMode()) {
        let a = config.callLocal(payload.api, {
          body: body
        })
        return a
      } else {
        return await API.call(payload.api, {
          headers: header,
          body: body
        })
      }
    },

    sendError({ state, dispatch }, payload) {
      let error = payload
      error.userAgent = window.navigator.userAgent
      error.platform = window.navigator.platform
      console.log(config.getVersion())
      dispatch('call', {
        api: 'postError',
        body: {
          category: 'web',
          user: state.username,
          version: config.getVersion(),
          stage: config.getStage(),
          date: new Date(),
          error: error,
          application: state.selectedApplication
        },
        public: true
      }).catch(
        (err) => {
          console.log(err)
        })
    }
  },

  // mutations
  mutations: {
    setCognitoUser(state, cognitoUser) {
      state.cognitUser = cognitoUser
      state.username = cognitoUser.username
    },

    setAttribute(state, attribute) {
      for (let i = 0; i < attribute.length; i++) {
        if (attribute[i].getName() == 'email') {
          state.email = attribute[i].getValue()
        } else if (attribute[i].getName() == 'name') {
          state.nickname = attribute[i].getValue()
        } else if (attribute[i].getName() == 'custom:application') {
          file.setApplication(attribute[i].getValue())
          state.application = attribute[i].getValue()
          state.isSuperUser = state.application === 'KM'
        } else if (attribute[i].getName() == 'custom:ai_auth') {
          state.ai_auth = attribute[i].getValue()
          if (state.ai_auth) {
            switch (state.ai_auth) {
            case 'specialist':
              state.isSpecialist = true
              break
            }
          }
        } else if (attribute[i].getName() == 'custom:role') {
          state.role = attribute[i].getValue()
          if (state.role) {
            switch (state.role) {
            case 'manager':
              state.isManager = true
              break
            case 'researcher':
              // 研究者は編集者＋αの機能を利用可能とするため、基本的には編集者モードで動作させる
              state.isEditor = true
              state.isResearcher = true
              break
            case 'editor':
              state.isEditor = true
              break
            case 'viewer':
              state.isViewer = true
              break
            }
          }
        } else if (attribute[i].getName() == 'sub') {
          state.sub = attribute[i].getValue()
        }
      }
      if (config.localMode()) {
        state.application = attribute['custom:application']
        file.setApplication(attribute['custom:application'])
      }
    },

    setDefinitions(state, definitions) {
      state.definitions = definitions
      /*let s = definitions['common:superuser']
      if (s && state.application === s.meaning.application) {
        state.isSuperUser = true
      }*/
      storage.setItem(storage.LocalStorageKeys.definitions, definitions)
    },

    setCurrentApplicationForSuperuser(state, application) {
      if (state.isSuperUser) {
        state.selectedApplication = application
        storage.setItemToday(storage.LocalStorageKeys.current_application, application)
      }
      file.setApplication(application)
    },

    setUserSession(state, session) {
      state.session = session
    },

    setUserAgent(state) {
      var userAgent = window.navigator.userAgent
      var device
      if(userAgent.indexOf('iPhone') != -1) {
        device = 'iPhone'
      } else if(userAgent.indexOf('Android') != -1) {
        if(userAgent.indexOf('Mobile') != -1) device = 'android'
      }
      if(device === 'iPhone' || device === 'android') {
        state.isMobile = true
      }
    },

    authenticated(state, result) {
      state.authenticated = result
    },

    clearAllState(state) {
      state.authenticated = false
      state.definitions = {}
      state.cognitoUser = null
      state.username = null
      state.application = null
      state.role = null
      state.ai_auth = null
      state.email = null
      state.nickname = null
      state.sub = null
      state.selectedApplication = null
      state.isSuperUser = false
      state.isManager = false
      state.isEditor = false
      state.isViewer = false
      state.isMobile = false
      state.isSpecialist = false
      state.session = null
    }
  }
}

export default auth